import { useSelector } from "@xstate/react";
import type { ActorRefFrom } from "xstate";
import React from "react";
import "./shell.css";

import { ListGroup, ListRow } from "@lattice/frontend/components/kit/List";
import { Sheet } from "@lattice/frontend/components/kit/Sheet";

import type { webSettingsMachine } from "./web-settings-machine";
import {
  getWebSettingsPanel,
  WEB_SETTINGS_PANEL_TITLES,
  type T_WebSettingsContext,
  type T_WebSettingsPanel,
} from "./web-settings-machine";
import { AccountPanel, DeleteAccountPanel } from "./settings/AccountPanel";
import { BrokeragesPanel } from "./settings/BrokeragesPanel";
import { InvestorProfilePanel } from "./settings/InvestorProfilePanel";
import { ManualHoldingsPanel } from "./settings/ManualHoldingsPanel";
import { NotificationsPanel } from "./settings/NotificationsPanel";
import { ReferralsPanel } from "./settings/ReferralsPanel";
import { SettingsRootPanel } from "./settings/SettingsRootPanel";
import { SubscriptionPanel } from "./settings/SubscriptionPanel";
import { UsagePanel } from "./settings/UsagePanel";

export type T_SettingsExternalDocument = "/eula" | "/privacy-policy" | "/terms";

export type T_SettingsOverlayProps = {
  actor: ActorRefFrom<typeof webSettingsMachine>;
  appEnvironment: string;
  logtoEndpoint: string;
  onOpenExternalDocument: (destination: T_SettingsExternalDocument) => void;
  paymentEnabled: boolean;
  revenueCatApiKey: string;
};

type T_SettingsOverlayViewProps = Pick<
  T_SettingsOverlayProps,
  "actor" | "onOpenExternalDocument"
> & {
  renderPanel: (
    panel: T_WebSettingsPanel,
    context: T_WebSettingsContext,
  ) => React.ReactNode;
};

/**
 * Web settings composition on the canonical kit Sheet. The web settings actor
 * owns every Lattice panel transition; only provider and legal-document
 * boundaries may leave this surface.
 */
export function SettingsOverlay({
  actor,
  appEnvironment,
  logtoEndpoint,
  onOpenExternalDocument,
  paymentEnabled,
  revenueCatApiKey,
}: T_SettingsOverlayProps) {
  const renderPanel = (
    panel: T_WebSettingsPanel,
    context: T_WebSettingsContext,
  ): React.ReactNode => {
    switch (panel) {
      case "root":
        return <SettingsRootPanel />;
      case "investorProfile":
        return <InvestorProfilePanel />;
      case "brokerages":
        return <BrokeragesPanel />;
      case "brokerageConnect":
        return <BrokeragesPanel initialConnectOpen />;
      case "account":
        return (
          <AccountPanel
            accountUpdate={context.accountUpdate}
            logtoEndpoint={logtoEndpoint}
          />
        );
      case "notifications":
        return <NotificationsPanel />;
      case "manualHoldings":
        return <ManualHoldingsPanel />;
      case "subscription":
        return (
          <SubscriptionPanel
            appEnvironment={appEnvironment}
            paymentEnabled={paymentEnabled}
            revenueCatApiKey={revenueCatApiKey}
          />
        );
      case "usage":
        return <UsagePanel />;
      case "referrals":
        return <ReferralsPanel initialRedemptionCode={context.referralCode} />;
      case "deleteAccount":
        return <DeleteAccountPanel />;
    }
  };

  return (
    <SettingsOverlayView
      actor={actor}
      onOpenExternalDocument={onOpenExternalDocument}
      renderPanel={renderPanel}
    />
  );
}

/** @storybook-internal Production composition with deterministic panel injection. */
export function SettingsOverlayView({
  actor,
  onOpenExternalDocument,
  renderPanel,
}: T_SettingsOverlayViewProps) {
  const snapshot = useSelector(actor, (current) => current);
  const open = snapshot.matches("settings");
  const panel = getWebSettingsPanel(snapshot.value);
  const title = WEB_SETTINGS_PANEL_TITLES[panel];

  return (
    <Sheet
      onClose={() => actor.send({ type: "CLOSE_SETTINGS" })}
      title={title}
      visible={open}
    >
      <div className="shell-settings">
        {panel !== "root" ? (
          <button
            className="shell-settings__back"
            onClick={() => actor.send({ type: "BACK" })}
            type="button"
          >
            ← Back
          </button>
        ) : null}
        <p className="shell-settings__title">{title}</p>

        {panel === "root" ? (
          <>
            {renderPanel("root", snapshot.context)}
            <ListGroup spaced>
              <ListRow
                onClick={() => actor.send({ type: "OPEN_INVESTOR_PROFILE" })}
                subtitle="Experience and interests"
                title="Investor profile"
              />
              <ListRow
                onClick={() => actor.send({ type: "OPEN_BROKERAGES" })}
                subtitle="View linked investment accounts"
                title="Brokerages"
              />
              <ListRow
                onClick={() => actor.send({ type: "OPEN_BROKERAGE_CONNECT" })}
                subtitle="Live sync via SnapTrade"
                title="Connect brokerage"
              />
            </ListGroup>

            <ListGroup spaced>
              <ListRow
                onClick={() => actor.send({ type: "OPEN_USAGE" })}
                subtitle="Monthly allowance and reset"
                title="Usage"
              />
              <ListRow
                onClick={() => actor.send({ type: "OPEN_ACCOUNT" })}
                subtitle="Notifications, holdings, plan, and account"
                title="Manage account"
              />
            </ListGroup>

            <ListGroup spaced>
              <ListRow
                onClick={() => onOpenExternalDocument("/privacy-policy")}
                subtitle="How Lattice protects and uses your data"
                title="Security and privacy"
              />
              <ListRow
                onClick={() => onOpenExternalDocument("/eula")}
                title="EULA"
              />
              <ListRow
                onClick={() => onOpenExternalDocument("/terms")}
                title="Terms and conditions"
              />
            </ListGroup>
          </>
        ) : null}

        {panel === "account" ? (
          <>
            <ListGroup>
              <ListRow
                onClick={() => actor.send({ type: "OPEN_NOTIFICATIONS" })}
                subtitle="Alerts and daily recaps"
                title="Notification preferences"
              />
              <ListRow
                onClick={() => actor.send({ type: "OPEN_MANUAL_HOLDINGS" })}
                subtitle="Positions tracked outside a brokerage"
                title="Manual holdings"
              />
              <ListRow
                onClick={() => actor.send({ type: "OPEN_SUBSCRIPTION" })}
                subtitle="Plan and billing"
                title="Subscription"
              />
              <ListRow
                onClick={() => actor.send({ type: "OPEN_REFERRALS" })}
                subtitle="Share Lattice and redeem a code"
                title="Referrals"
              />
            </ListGroup>
            <ListGroup spaced>
              <ListRow
                onClick={() => actor.send({ type: "OPEN_DELETE_ACCOUNT" })}
                title="Delete account"
                tone="danger"
              />
            </ListGroup>
            {renderPanel("account", snapshot.context)}
          </>
        ) : null}

        {panel !== "root" && panel !== "account"
          ? renderPanel(panel, snapshot.context)
          : null}
      </div>
    </Sheet>
  );
}
